import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { APIServer } from '@/app/data/api'
import type { VoiceFile } from '../types'
import { isApp } from '@/index'
import {
  buildVoiceFilePath,
  downloadLocalVoiceFile,
  readLocalVoiceFileAsFile,
} from '@/app/function/voice'
import { Network } from '@capacitor/network'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { SyncManager } from '../../sync/syncManager'
import { getRandomString } from '../../../lib'
import { CloudFile } from '../../../lib/CloudFile'
import { JobPriority, JobStatus, syncQueue } from '../../sync'
import { filterStaleMergedItems } from '../../sync/mergeWriteGuard'
import { isRowDeletedLocally } from '../../sync/jobGuards'
import { SQL } from '../../../lib/Sqlite'
import { prepareSQLiteValue } from '../../../lib/Sqlite/schema'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { AppFileManager } from '../../file'
import { parseRow } from '@/app/util'

const storeKey = 'voice_files'

export const useVoiceFileStore = defineStore(storeKey, () => {
  const voiceFiles = ref<VoiceFile[]>([])
  const currentTaskDatabaseId = ref<string>('')
  // 과업 진입은 initData를 기다리지 않는다 — 늦게 끝난 이전 회차가 새 과업의 목록·로컬 DB를 덮지 않게 하는 세대
  let initGeneration = 0

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedVoiceFiles = computed(() => {
    return voiceFiles.value.filter(item => item.modifiedFromLocal)
  })

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제 (중복 등록 방지)
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('voice_files', currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          // 대량 업데이트: 전체 로드
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          voiceFiles.value = localData.values?.map((row: unknown) => parseRow(row)) as VoiceFile[]
        } else {
          // 개별 업데이트: 해당 항목만 갱신
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )

            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as VoiceFile
              const index = voiceFiles.value.findIndex(v => v.localStringId === id)

              if (updatedItem.deleted_at) {
                // 삭제된 항목은 배열에서 제거
                if (index !== -1) voiceFiles.value.splice(index, 1)
              } else if (index !== -1) {
                // 기존 항목 업데이트
                voiceFiles.value[index] = updatedItem
              } else {
                // 새 항목 추가
                voiceFiles.value.push(updatedItem)
              }
            } else {
              // 쿼리 결과 없음 = 삭제됨
              const index = voiceFiles.value.findIndex(v => v.localStringId === id)
              if (index !== -1) voiceFiles.value.splice(index, 1)
            }
          }
        }
      } catch (error) {
        console.error('[VoiceFileStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('voice_files', currentListener)
  }

  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedVoiceFiles.value.length > 0) {
      await requestUpdate(notUploadedVoiceFiles.value)
    }
  }
  async function individualSetLocalModified(
    localStringId: string, // 시그니처 변경
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()
    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      if (i < keyValues.length - 1) {
        sql += ', '
      }
    }
    sql += ' WHERE localStringId = ?' // WHERE 절 변경
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    // 이벤트 발행 (리스너 완료 대기)
    await SQLiteEventBus.getInstance().emitUpdate('voice_files', 'update', [localStringId])
  }
  async function getServerData(taskId: string): Promise<VoiceFile[]> {
    if (!taskId) return []
    try {
      const data: VoiceFile[] = await APIServer.getVoiceFileList({
        task_id: Number(taskId),
        order: 'created_at',
      })
      return data.map(item => {
        return {
          ...item,
          localStringId: String(item.id),
          modifiedFromLocal: false,
        } as VoiceFile
      })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  async function initData(taskId: string): Promise<void> {
    const gen = ++initGeneration
    currentTaskDatabaseId.value = taskId
    setupSQLiteListener(taskId)
    let localData: VoiceFile[] = []
    let mergedData: VoiceFile[] = []
    const networkStatus = await Network.getStatus()
    if (isApp()) {
      localData = (await SQLite.getLocalData(storeKey, 'task_id', taskId, true)) as VoiceFile[]
      // 초기값은 로컬 데이터
      mergedData = localData
    }
    if (networkStatus.connected) {
      return await getServerData(taskId)
        .then(async serverData => {
          if (!isApp()) {
            return Merge.sortupdatedAt(serverData)
          }
          // 3. 테이블 생성 처리
          if (!(await SQLite.checkTableExists(storeKey, serverData))) {
            return serverData
          }

          return serverData
        })
        .then(async serverData => {
          if (!isApp()) {
            return serverData
          }
          // 서버가 빈 배열인데 로컬에 비삭제 데이터가 있으면 merge 스킵 (데이터 소실 방지)
          const activeLocalData = localData.filter(item => !item.deleted_at)
          if (serverData.length > 0 || activeLocalData.length === 0) {
            mergedData = Merge.mergeBothData(localData, serverData, storeKey)
          }
          return mergedData
        })
        .then(async mergedData => {
          // 그사이 다른 과업(또는 같은 과업 재진입)의 준비가 시작됐으면 쓰지 않는다
          if (gen !== initGeneration) return
          // 5. 스토어 업데이트
          if (mergedData.length > 0) {
            voiceFiles.value = mergedData
            if (isApp()) {
              Promise.all(mergedData.map(voiceFile => downloadLocalVoiceFile(voiceFile))).catch(
                error => {
                  console.error('Error downloading voice files:', error)
                },
              )
            }
            if (!isApp()) return
            const isAlreadySyncing = SyncManager.isTaskSyncing(taskId)
            if (!isAlreadySyncing) {
              // 병합 결과 저장: 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 제외
              // (유령 row 부활 → 재업로드로 인한 서버 중복 방지)
              const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
                mergedData,
                storeKey,
              )
              const safeItems = await filterStaleMergedItems(
                itemsToProcess as { item: VoiceFile; itemId: string }[],
                storeKey,
                taskId,
              )
              await SQLite.updateSqliteOnly(safeItems, columns, taskId, storeKey)
              await addApiQueue(safeItems)
            } else {
              console.info(
                `[VoiceStore] taskId ${taskId}: backgroundSync 진행 중 - requestUpdate 스킵 (중복 방지)`,
              )
            }
          }
        })
        .catch(error => {
          throw error
        })
    } else {
      if (gen !== initGeneration) return
      const sortData = Merge.sortupdatedAt(mergedData)
      voiceFiles.value = sortData
    }
  }
  const requestUpdate = async (datas: VoiceFile[]): Promise<void> => {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(datas, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: VoiceFile; itemId: string }[])
  }
  const addApiQueue = async (
    itemsToProcess: { item: VoiceFile; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return

    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼습니다.
      if (!item.modifiedFromLocal) continue
      addVoiceRecordJobToQueue(item)
    }
  }
  async function addVoiceRecordJobToQueue(item: VoiceFile): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    const isDeleted = item.deleted_at
    const needsFileUpload = !item.url.startsWith('storage://')

    let executeCallback: (data: VoiceFile) => Promise<VoiceFile | void>
    let jobPriority = JobPriority.NORMAL

    const existingJobs = syncQueue
      .getQueueState()
      .filter(
        job =>
          job.localStringId === item.localStringId &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING),
      )

    // 삭제 요청은 진행 중인 업로드 job이 있어도 큐에 넣어야 한다
    // (업로드 완료 후 삭제가 이어서 서버로 전파되도록 — 스킵하면 서버에 파일이 남음)
    if (existingJobs.length > 0 && !isDeleted) {
      console.warn(
        `[VoiceFile] Job for ${item.localStringId} already exists in queue. Skipping duplicate.`,
      )
      return
    }

    if (isDeleted) {
      // 서버에 생성된 적 없고 진행 중인 업로드 job도 없는 파일은 서버에 알릴 필요가 없음
      if (isNew && existingJobs.length === 0) {
        await individualSetLocalModified(item.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data: VoiceFile): Promise<void> => {
        // 실행 시점 기준 서버 미생성 파일(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return
        }
        await APIServer.deleteVoiceFiles([{ id: Number(data.id) }])
        // onSuccess의 mfl 정리는 enqueue 시점 isNew에 의존하므로 여기서 직접 완료 처리
        await individualSetLocalModified(data.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
      }
    } else if (isNew) {
      jobPriority = JobPriority.LOW // 파일 업로드는 우선순위 낮춤
      executeCallback = async (data: VoiceFile): Promise<VoiceFile> => {
        // 업로드 전에 로컬에서 삭제된 파일: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        // 신규 로직: 파일 업로드를 먼저 수행
        if (needsFileUpload) {
          if (!(await AppFileManager.checkFileExists(data.url))) {
            console.warn(
              `[VoiceFile] File not found: ${data.url}. Marking as deleted instead of retrying.`,
            )
            // 파일이 없으면 삭제 처리하고 업로드 시도하지 않음
            await individualSetLocalModified(data.localStringId, [
              { key: 'deleted_at', value: Merge.getCurrentTime() },
              { key: 'modifiedFromLocal', value: false },
            ])
            // 삭제 처리된 데이터를 반환 (job 성공으로 처리)
            return { ...data, deleted_at: Merge.getCurrentTime(), modifiedFromLocal: false }
          }

          const file = await readLocalVoiceFileAsFile(data.url, 'audio/mp4')
          const gcsPath = buildVoiceFilePath({
            taskId: data.task_id,
            facilityId: data.facility_id,
            templateId: data.template_id || undefined,
            recordItemKey: data.record_item_key,
            fileName: data.fileName,
          })
          const resourceUrl = await CloudFile.uploadFile('homecheck-service', gcsPath, file)
          if (!resourceUrl) throw new Error('Cloud upload failed for new voice file.')

          await individualSetLocalModified(data.localStringId, [{ key: 'url', value: resourceUrl }])

          const voiceFileData: Partial<VoiceFile> = {
            ...data,
            url: resourceUrl,
          }
          const [voiceFileResult] = await APIServer.addVoiceFiles([
            Merge.returnHasValue(voiceFileData) as VoiceFile,
          ])

          const resultData = {
            ...data,
            ...voiceFileResult,
            id: voiceFileResult.id,
            url: resourceUrl,
          }
          return resultData
        }
        const voiceFileData: Partial<VoiceFile> = {
          ...data,
          url: data.url,
        }
        const [voiceFileResult] = await APIServer.addVoiceFiles([
          Merge.returnHasValue(voiceFileData) as VoiceFile,
        ])
        const resultData = { ...data, ...voiceFileResult, id: voiceFileResult.id, url: data.url }
        return resultData
      }
    } else {
      // 기존 항목 업데이트는 지원하지 않음 (음성 파일은 추가/삭제만 가능)
      console.warn('[VoiceFile] Update for existing items is not supported. Skipping.')
      return
    }
    syncQueue.addJob({
      localStringId: item.localStringId,
      data: item,
      priority: jobPriority,
      maxRetries: 3,
      tag: 'voiceFile',
      execute: executeCallback,

      onSuccess: async (result, data) => {
        if (!isNew) {
          const updateValues = [{ key: 'modifiedFromLocal', value: false }]
          if (needsFileUpload && result?.url) {
            updateValues.push({ key: 'url', value: result.url })
          }
          await individualSetLocalModified(data.localStringId, updateValues)
        }
      },

      onError: (error, data) => {
        console.error(`[SyncQueue] Voice file job for ${data.localStringId} FAILED.`, error)
      },

      onIdMap: isNew
        ? async (localStringId: string, serverResult: VoiceFile): Promise<void> => {
            // 중요: 데이터베이스에서 중복된 항목이 있는지 확인하고 제거
            const db = await SQL.getDatabase()
            const existingItems = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ?`,
              [localStringId],
            )

            if (existingItems.values && existingItems.values.length > 1) {
              console.warn(
                `[SyncQueue] Found ${existingItems.values.length} duplicate items for ${localStringId}. Cleaning up...`,
              )
              // 가장 최신 항목을 제외한 나머지는 삭제
              await db.execute(
                `DELETE FROM ${storeKey} WHERE localStringId = ? AND id LIKE '%_need_to_upload'`,
                [localStringId],
              )
            }

            // 메모리 상의 데이터 업데이트
            const voiceFileInStore = voiceFiles.value.find(p => p.localStringId === localStringId)
            if (voiceFileInStore) {
              voiceFileInStore.id = serverResult.id
              voiceFileInStore.url = serverResult.url
              voiceFileInStore.modifiedFromLocal = false
            }

            // 데이터베이스 업데이트
            // (업로드 도중 로컬 삭제가 끼어든 경우 mfl을 유지해 삭제 job이 이어서 전파)
            const idMapKeys: { key: string; value: unknown }[] = [
              { key: 'id', value: serverResult.id },
              { key: 'url', value: serverResult.url },
            ]
            if (!(await isRowDeletedLocally(storeKey, localStringId))) {
              idMapKeys.push({ key: 'modifiedFromLocal', value: false })
            }
            await individualSetLocalModified(localStringId, idMapKeys)
          }
        : undefined,
    })
  }

  async function addRecord(
    urlParam: string, //녹음파일
    taskId: number,
    facilityId: number,
    displayName: string,
    durationMs: number,
    fileName: string,
    templateId?: number,
    recordItemKey?: string,
  ): Promise<void> {
    const randomId = getRandomString(12, false)
    const voiceFileData: Partial<VoiceFile> = {
      id: `${randomId}_need_to_upload`,
      task_id: taskId,
      facility_id: facilityId,
      name: displayName,
      duration_ms: durationMs,
      url: urlParam,
      fileName: fileName,
      localStringId: `${randomId}`,
      created_at: Merge.getCurrentUTCTime(),
      modifiedFromLocal: true,
    }
    if (templateId) voiceFileData.template_id = templateId
    if (recordItemKey) voiceFileData.record_item_key = recordItemKey
    if (!isApp()) {
      voiceFiles.value.push({
        ...voiceFileData,
      } as VoiceFile)
    }

    await requestUpdate([voiceFileData as VoiceFile])
  }

  async function deleteRecord(id: string): Promise<void> {
    const findVoiceFile = voiceFiles.value.find(v => v.localStringId == id || v.id == id)
    if (!findVoiceFile) return

    if (isApp()) {
      const target = { ...findVoiceFile }
      target.deleted_at = Merge.getCurrentTime()
      target.modifiedFromLocal = true
      await requestUpdate([target])
    } else {
      await APIServer.deleteVoiceFiles([{ id: Number(findVoiceFile.id) }])
    }
    if (!isApp()) {
      voiceFiles.value = voiceFiles.value.map(v => {
        if (v.localStringId == id || v.id == id) {
          return { ...v, deleted_at: Merge.getCurrentTime(), modifiedFromLocal: false }
        }
        return v
      })
    }
  }

  // 특정 조건으로 필터링된 음성파일 목록 반환
  const getVoiceFilesByParams = computed(() => {
    return (params: {
      facility_id?: number
      floorplan_id?: number
      defect_id?: number
      template_id?: number
      record_item_key?: string
    }): VoiceFile[] => {
      return voiceFiles.value.filter(voiceFile => {
        if (params.facility_id !== undefined && voiceFile.facility_id !== params.facility_id)
          return false
        if (params.floorplan_id !== undefined && voiceFile.floorplan_id !== params.floorplan_id)
          return false
        if (params.defect_id !== undefined && voiceFile.defect_id !== params.defect_id) return false
        if (params.template_id !== undefined && voiceFile.template_id !== params.template_id)
          return false
        if (
          params.record_item_key !== undefined &&
          voiceFile.record_item_key !== params.record_item_key
        )
          return false
        return true
      })
    }
  })

  const getVoiceFilesByTaskId = computed(() => {
    return (taskId: number): VoiceFile[] => {
      return voiceFiles.value
        .filter(voiceFile => voiceFile.task_id == taskId && !voiceFile.deleted_at)
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
    }
  })

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('voice_files', currentListener)
      currentListener = null
    }
    voiceFiles.value = []
  }

  return {
    voiceFiles,
    initData,
    getVoiceFilesByParams,
    addRecord,
    uploadNotUploaded,
    notUploadedVoiceFiles,
    individualSetLocalModified,
    deleteRecord,
    getVoiceFilesByTaskId,
    allClear,
  }
})
